<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AgentsAPI from 'dashboard/api/agents';
import ContactsAPI from 'dashboard/api/contacts';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import TalkviaCalendarEventsAPI from 'dashboard/api/talkviaCalendarEvents';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { isAdmin } = useAdmin();
const inboxes = useMapGetter('inboxes/getInboxes');
const AUTO_REFRESH_INTERVAL_MS = 20000;

const copy = {
  title: 'Agenda',
  subtitle:
    'Organize retornos, reuniões e compromissos vindos das conversas, propostas e integrações externas.',
  actions: {
    newEvent: 'Novo evento',
    create: 'Criar',
    saveEvent: 'Salvar evento',
    cancel: 'Cancelar',
    edit: 'Editar',
    sync: 'Sincronizar',
    menu: 'Menu',
    search: 'Pesquisar',
    clearSearch: 'Limpar busca',
    clearFilters: 'Limpar',
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    year: 'Ano',
    schedule: 'Programação',
    fourDays: '4 dias',
    today: 'Hoje',
    previous: 'Anterior',
    next: 'Próximo',
    filter: 'Filtrar',
    filters: 'Filtros',
    applyFilters: 'Aplicar filtros',
    apply: 'Aplicar',
    configureGoogle: 'Configurar Google Agenda',
    close: 'Fechar',
  },
  fields: {
    title: 'Título',
    description: 'Descrição',
    location: 'Local',
    start: 'Início',
    end: 'Fim',
    period: 'Período do evento',
    status: 'Status',
    source: 'Origem',
    assignee: 'Responsável',
    inbox: 'Caixa de entrada',
    contact: 'Contato',
    contactSearch: 'Buscar contato',
    inventoryProduct: 'Produto do serviço',
    inventoryQuantity: 'Quantidade para baixa',
    createGoogleMeet: 'Criar link Google Meet',
    attendeeEmails: 'Convidados por e-mail',
    googleMeetLink: 'Link Google Meet',
    allDay: 'Dia inteiro',
  },
  sections: {
    filters: 'Filtros',
    upcoming: 'Próximos eventos',
    eventDetails: 'Detalhes do evento',
    eventDetailsDescription:
      'Informe o assunto e quando o compromisso acontece.',
    organization: 'Organização',
    organizationDescription:
      'Vincule o evento ao atendimento e ao local correto.',
    service: 'Serviço',
    serviceDescription:
      'Vincule um item do catálogo e informe a quantidade que será baixada.',
    meeting: 'Videoconferência',
    meetingDescription:
      'Gere um link do Google Meet e envie o convite aos participantes.',
    notes: 'Observações',
    googleIntegration: 'Integração do Google Agenda',
  },
  labels: {
    all: 'Todos',
    allSources: 'Todas',
    activeFilters: 'filtro(s) ativo(s)',
    allEvents: 'Mostrando todos os eventos',
    allInboxes: 'Todas as caixas',
    noAssignee: 'Sem responsável',
    noInbox: 'Nenhuma caixa',
    noProduct: 'Nenhum produto',
    connected: 'Google Agenda conectado',
    googleUnavailable: 'Google Agenda não conectado',
    emptyValue: '-',
    more: '+',
    cards: {
      month: 'Eventos no mês',
      today: 'Hoje',
      snoozed: 'Adiados',
      active: 'Ativo',
      inactive: 'Inativo',
    },
  },
  weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  placeholders: {
    title: 'Ex: Retorno para proposta FGTS',
    contactSearch: 'Nome, telefone ou e-mail',
    location: 'Ex: Loja, escritório ou endereço',
    inventoryQuantity: 'Ex: 1',
    attendeeEmails: 'cliente@email.com, equipe@email.com',
    notes: 'Contexto, pauta ou instruções para o atendimento',
    period: 'Selecione o início e o fim do compromisso',
    periodHelp: 'Selecione a data e o horário de início e fim do compromisso.',
  },
  statuses: {
    scheduled: 'Agendado',
    completed: 'Concluído',
    canceled: 'Cancelado',
    no_show: 'Não compareceu',
  },
  sources: {
    manual: 'Manual',
    snooze: 'Conversa adiada',
    proposal: 'Proposta',
    google: 'Google Agenda',
    external: 'Sistema externo',
    contract: 'Contrato',
  },
  contractModal: {
    label: 'Contrato',
    modelo: 'Modelo',
    contato: 'Contato',
    dataEntrega: 'Data de entrega',
    dataRetirada: 'Data de retirada',
    formaPagamento: 'Forma de pagamento',
    valorTotal: 'Valor total',
    telefone: 'Telefone',
    endereco: 'Endereço',
    produtos: 'Produtos',
    produto: 'Produto',
    qtd: 'Qtd.',
    valorUnit: 'Valor unit.',
    subtotal: 'Subtotal',
    fechar: 'Fechar',
    verContrato: 'Ver contrato',
    editarContrato: 'Editar contrato',
    verContato: 'Ver contato',
    mapas: 'Rotas',
    abrirGoogleMaps: 'Google Maps',
    abrirWaze: 'Waze',
    enviarRota: 'Enviar rota',
    enviarPara: 'Enviar para',
    buscarContato: 'Buscar contato',
    caixaEntrada: 'Caixa de entrada',
    selecionarCaixaEntrada: 'Selecione a caixa de entrada',
    buscandoContatos: 'Buscando contatos...',
    copiarEndereco: 'Copiar endereço',
    alterarStatus: 'Alterar status',
    confirmar: 'Confirmar',
  },
  empty: {
    events: 'Nenhum evento nesse período.',
    search: 'Nenhum evento encontrado.',
    contacts: 'Nenhum contato encontrado.',
  },
  messages: {
    loadFailed: 'Não foi possível carregar a agenda.',
    saveFailed: 'Não foi possível salvar o evento.',
    saved: 'Evento salvo.',
    googleIntegrationRequired:
      'Conecte e ative o Google Agenda para criar o link do Google Meet.',
    googleIntegrationLoadFailed:
      'Não foi possível verificar a integração do Google Agenda agora.',
  },
};

const today = new Date();
const currentDate = ref(new Date(today));
const events = ref([]);
const contractEvents = ref([]);
const inventoryProducts = ref([]);
const agents = ref([]);
const contactResults = ref([]);
const contactSearch = ref('');
const integration = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const editingEventId = ref(null);
const eventDialogRef = ref(null);
const showContractModal = ref(false);
const showRouteSendModal = ref(false);
const selectedContract = ref(null);
const selectedContractEventType = ref('');
const selectedContactDetails = ref(null);
const contractStatusChanging = ref(false);
const pendingContractStatus = ref('');
const mapProviderToSend = ref('google');
const mapContactSearch = ref('');
const mapContactResults = ref([]);
const selectedMapContactId = ref('');
const selectedMapInboxId = ref('');
const isSearchingMapContacts = ref(false);
const refreshTimer = ref(null);
const isRefreshing = ref(false);
const isSidePanelOpen = ref(true);
const isSearchOpen = ref(false);
const isViewMenuOpen = ref(false);
const isFiltersOpen = ref(false);
const integrationLoadFailed = ref(false);
const searchQuery = ref('');
const searchInputRef = ref(null);
const searchTimer = ref(null);
const viewMode = ref('month');
const draggedCalendarEvent = ref(null);
const dragOverCalendarKey = ref('');

const viewOptions = [
  { value: 'day', label: copy.actions.day, shortcut: 'D' },
  { value: 'week', label: copy.actions.week, shortcut: 'W' },
  { value: 'month', label: copy.actions.month, shortcut: 'M' },
  { value: 'year', label: copy.actions.year, shortcut: 'Y' },
  { value: 'schedule', label: copy.actions.schedule, shortcut: 'A' },
  { value: 'fourDays', label: copy.actions.fourDays, shortcut: 'X' },
];

const filters = reactive({
  status: '',
  source: '',
  assignee_id: '',
  inbox_id: '',
});
const filterDraft = reactive({ ...filters });

const activeFilterCount = computed(
  () => Object.values(filters).filter(value => String(value).length > 0).length
);

const eventForm = reactive({
  title: '',
  description: '',
  location: '',
  starts_at: '',
  ends_at: '',
  all_day: false,
  status: 'scheduled',
  source: 'manual',
  contact_id: '',
  conversation_id: '',
  inbox_id: '',
  assignee_id: '',
  metadata: {},
});

const statusOptions = computed(() =>
  Object.entries(copy.statuses).map(([value, label]) => ({ value, label }))
);
const sourceOptions = computed(() =>
  Object.entries(copy.sources).map(([value, label]) => ({ value, label }))
);
const inboxOptions = computed(() => inboxes.value || []);
const assigneeOptions = computed(() => [
  { value: '', label: copy.labels.noAssignee },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent?.name || agent?.available_name || agent?.email || '',
  })),
]);
const eventInboxOptions = computed(() => [
  { value: '', label: copy.labels.noInbox },
  ...inboxOptions.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);
const productOptions = computed(() => [
  { value: '', label: copy.labels.noProduct },
  ...inventoryProducts.value.map(product => ({
    value: product.id,
    label: product.name,
  })),
]);
const filterStatusOptions = computed(() => [
  { value: '', label: copy.labels.all },
  ...statusOptions.value,
]);
const filterSourceOptions = computed(() => [
  { value: '', label: copy.labels.allSources },
  ...sourceOptions.value,
]);
const filterAssigneeOptions = computed(() => [
  { value: '', label: copy.labels.all },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent?.name || agent?.available_name || agent?.email || '',
  })),
]);
const filterInboxOptions = computed(() => [
  { value: '', label: copy.labels.allInboxes },
  ...inboxOptions.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);
const isGoogleCalendarActive = computed(() =>
  Boolean(integration.value?.active && integration.value?.has_credentials)
);
const googleMeetLink = computed(
  () => eventForm.metadata?.google_meet_link || ''
);
const eventStartDate = computed(() =>
  eventForm.starts_at ? new Date(eventForm.starts_at) : null
);
const eventEndDate = computed(() =>
  eventForm.ends_at ? new Date(eventForm.ends_at) : null
);
const monthLabel = computed(() =>
  new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
  }).format(currentDate.value)
);

const weekStart = computed(() => {
  const date = new Date(currentDate.value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
});

const weekEnd = computed(() => {
  const date = new Date(weekStart.value);
  date.setDate(date.getDate() + 6);
  date.setHours(23, 59, 59, 999);
  return date;
});

const dayStart = computed(() => {
  const date = new Date(currentDate.value);
  date.setHours(0, 0, 0, 0);
  return date;
});

const dayEnd = computed(() => {
  const date = new Date(dayStart.value);
  date.setHours(23, 59, 59, 999);
  return date;
});

const fourDaysEnd = computed(() => {
  const date = new Date(dayStart.value);
  date.setDate(date.getDate() + 3);
  date.setHours(23, 59, 59, 999);
  return date;
});

const weekRangeLabel = computed(() => {
  const start = weekStart.value;
  const end = weekEnd.value;
  const sameMonth = start.getMonth() === end.getMonth();
  const monthFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  if (sameMonth) return monthFormatter.format(start);

  const startLabel = new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
  }).format(start);
  const endLabel = monthFormatter.format(end);
  return `${startLabel} - ${endLabel}`;
});

const rangeLabel = computed(() => {
  const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  if (viewMode.value === 'day') return dateFormatter.format(currentDate.value);
  if (viewMode.value === 'fourDays') {
    return `${dateFormatter.format(dayStart.value)} - ${dateFormatter.format(
      fourDaysEnd.value
    )}`;
  }
  if (viewMode.value === 'month') return monthLabel.value;
  if (viewMode.value === 'year') return String(currentDate.value.getFullYear());
  if (viewMode.value === 'schedule') return 'Programação';
  return weekRangeLabel.value;
});

const currentViewLabel = computed(
  () =>
    viewOptions.find(option => option.value === viewMode.value)?.label ||
    copy.actions.week
);

const monthStart = computed(
  () =>
    new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1)
);

const monthEnd = computed(() => {
  const date = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    0
  );
  date.setHours(23, 59, 59, 999);
  return date;
});

const yearStart = computed(
  () => new Date(currentDate.value.getFullYear(), 0, 1)
);

const yearEnd = computed(() => {
  const date = new Date(currentDate.value.getFullYear(), 11, 31);
  date.setHours(23, 59, 59, 999);
  return date;
});

const requestRange = computed(() => {
  if (viewMode.value === 'day') {
    return { start: dayStart.value, end: dayEnd.value };
  }
  if (viewMode.value === 'fourDays') {
    return { start: dayStart.value, end: fourDaysEnd.value };
  }
  if (viewMode.value === 'month') {
    return { start: monthStart.value, end: monthEnd.value };
  }
  if (viewMode.value === 'year') {
    return { start: yearStart.value, end: yearEnd.value };
  }
  if (viewMode.value === 'schedule') {
    const end = new Date(dayStart.value);
    end.setMonth(end.getMonth() + 3);
    end.setHours(23, 59, 59, 999);
    return { start: dayStart.value, end };
  }
  return { start: weekStart.value, end: weekEnd.value };
});

const toDateKey = value => {
  const date = value instanceof Date ? value : new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    .toISOString()
    .slice(0, 10);
};

const calendarDays = computed(() => {
  const start = new Date(monthStart.value);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      date,
      key: toDateKey(date),
      currentMonth: date.getMonth() === currentDate.value.getMonth(),
      today: date.toDateString() === today.toDateString(),
    };
  });
});

const visibleDays = computed(() => {
  const amount =
    {
      day: 1,
      fourDays: 4,
      week: 7,
    }[viewMode.value] || 7;
  const start = viewMode.value === 'week' ? weekStart.value : dayStart.value;

  return Array.from({ length: amount }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      date,
      key: toDateKey(date),
      weekday: copy.weekdays[date.getDay()],
      day: date.getDate(),
      today: date.toDateString() === today.toDateString(),
    };
  });
});

const yearMonths = computed(() =>
  Array.from({ length: 12 }, (_, index) => {
    const date = new Date(currentDate.value.getFullYear(), index, 1);
    return {
      date,
      key: date.toISOString().slice(0, 7),
      label: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(date),
    };
  })
);

const timeSlots = Array.from({ length: 24 }, (_, hour) => hour);

const allCalendarItems = computed(() => {
  const showContracts = filters.source === '' || filters.source === 'contract';
  return [...events.value, ...(showContracts ? contractEvents.value : [])];
});

const visibleCalendarItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return allCalendarItems.value;

  return allCalendarItems.value.filter(event =>
    [
      event.title,
      event.description,
      event.location,
      copy.sources[event.source],
      event.contact?.name,
      event.contact?.phone_number,
      event.contractData?.recipient_name,
      event.contractData?.template_name,
    ]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query))
  );
});

const upcomingEvents = computed(() =>
  [...visibleCalendarItems.value]
    .filter(event => new Date(event.starts_at) >= new Date())
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
    .slice(0, 8)
);

const scheduleEvents = computed(() =>
  [...visibleCalendarItems.value].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at)
  )
);

const searchResults = computed(() => {
  if (searchQuery.value.trim().length < 2) return [];
  return scheduleEvents.value.slice(0, 8);
});

const eventsForDay = date => {
  const key = toDateKey(date);
  return visibleCalendarItems.value.filter(
    event => toDateKey(event.starts_at) === key
  );
};

const calendarWeeks = computed(() => {
  const result = [];
  for (let i = 0; i < calendarDays.value.length; i += 7) {
    result.push(calendarDays.value.slice(i, i + 7));
  }
  return result;
});

const isMultiDayEvent = event => {
  if (!event.starts_at || !event.ends_at) return false;
  return toDateKey(event.starts_at) !== toDateKey(event.ends_at);
};

const getWeekMultiDayLayout = week => {
  const weekStartKey = week[0].key;
  const weekEndKey = week[6].key;

  const multiDayEvents = visibleCalendarItems.value
    .filter(event => {
      if (!isMultiDayEvent(event)) return false;

      const startKey = toDateKey(event.starts_at);
      const endKey = toDateKey(event.ends_at);
      return startKey <= weekEndKey && endKey >= weekStartKey;
    })
    .sort((a, b) => {
      const startSort = a.starts_at.localeCompare(b.starts_at);
      if (startSort !== 0) return startSort;
      return b.ends_at.localeCompare(a.ends_at);
    });

  const rowEnds = [];

  return multiDayEvents.map(event => {
    const startKey = toDateKey(event.starts_at);
    const endKey = toDateKey(event.ends_at);
    const startCol =
      startKey < weekStartKey ? 0 : week.findIndex(day => day.key === startKey);
    const endCol =
      endKey > weekEndKey ? 6 : week.findIndex(day => day.key === endKey);
    const resolvedStartCol = startCol === -1 ? 0 : startCol;
    const resolvedEndCol = endCol === -1 ? 6 : endCol;

    let row = 0;
    while (rowEnds[row] !== undefined && rowEnds[row] >= resolvedStartCol) {
      row += 1;
    }
    rowEnds[row] = resolvedEndCol;

    return {
      event,
      row,
      startCol: resolvedStartCol,
      colSpan: resolvedEndCol - resolvedStartCol + 1,
      startsThisWeek: startKey >= weekStartKey,
      endsThisWeek: endKey <= weekEndKey,
    };
  });
};

const weekLayouts = computed(() =>
  calendarWeeks.value.map(week => getWeekMultiDayLayout(week))
);

const weekMultiDayRows = weekIndex => {
  const layout = weekLayouts.value[weekIndex] || [];
  if (!layout.length) return 0;
  return Math.max(...layout.map(item => item.row)) + 1;
};

const singleDayEventsForDate = date => {
  const key = toDateKey(date);
  return visibleCalendarItems.value
    .filter(
      event => toDateKey(event.starts_at) === key && !isMultiDayEvent(event)
    )
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
};

const monthEventsForDate = date => singleDayEventsForDate(date);

const hiddenMonthEventsCount = date =>
  Math.max(monthEventsForDate(date).length - 4, 0);

const eventDateLabel = value => {
  if (!value) return '';
  const date = new Date(value);
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}`;
};

const multiDayEventLabel = event =>
  `${eventDateLabel(event.starts_at)} até ${eventDateLabel(event.ends_at)}`;

const eventsForHour = (date, hour) =>
  eventsForDay(date)
    .filter(event => new Date(event.starts_at).getHours() === hour)
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

const eventsForMonth = date =>
  visibleCalendarItems.value.filter(event => {
    const eventDate = new Date(event.starts_at);
    return (
      eventDate.getFullYear() === date.getFullYear() &&
      eventDate.getMonth() === date.getMonth()
    );
  });

const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';
const formatTime = value =>
  value
    ? new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
const formatHour = hour => {
  if (hour === 0) return 'GMT-03';
  return `${String(hour).padStart(2, '0')}:00`;
};

const dateInputValue = date => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const calendarDropKey = (date, hour = null) =>
  hour === null ? toDateKey(date) : `${toDateKey(date)}-${hour}`;

const isDraggableEvent = event =>
  event?.source !== 'contract' || Boolean(event?.contractData?.id);

const clearCalendarDragState = () => {
  draggedCalendarEvent.value = null;
  dragOverCalendarKey.value = '';
};

const startCalendarEventDrag = (event, dragEvent) => {
  if (!isDraggableEvent(event)) {
    dragEvent.preventDefault();
    return;
  }

  draggedCalendarEvent.value = event;
  dragEvent.dataTransfer.effectAllowed = 'move';
  dragEvent.dataTransfer.setData('text/plain', String(event.id));
};

const handleCalendarEventDragOver = (date, dragEvent, hour = null) => {
  if (!draggedCalendarEvent.value) return;
  dragEvent.preventDefault();
  dragEvent.dataTransfer.dropEffect = 'move';
  dragOverCalendarKey.value = calendarDropKey(date, hour);
};

const handleCalendarEventDragLeave = (date, hour = null) => {
  if (dragOverCalendarKey.value === calendarDropKey(date, hour)) {
    dragOverCalendarKey.value = '';
  }
};

const updateLocalCalendarEvent = updatedEvent => {
  const index = events.value.findIndex(
    event => String(event.id) === String(updatedEvent.id)
  );
  if (index !== -1)
    events.value[index] = { ...events.value[index], ...updatedEvent };
};

const updateLocalContractCalendarEvent = (event, payload) => {
  const contractId = event.contractData?.id;
  if (!contractId) return;

  const updatedContract = { ...event.contractData, ...payload };
  contractEvents.value = contractEvents.value.map(item =>
    item.contractData?.id === contractId
      ? {
          ...item,
          starts_at: payload.scheduled_at || item.starts_at,
          ends_at: payload.pickup_at || payload.scheduled_at || item.ends_at,
          contractData: updatedContract,
        }
      : item
  );

  if (selectedContract.value?.id === contractId) {
    selectedContract.value = { ...selectedContract.value, ...payload };
  }
};

const moveContractCalendarEvent = async (event, movedStart, originalStart) => {
  const originalEnd = event.ends_at ? new Date(event.ends_at) : null;
  const payload = {};

  if (event.contractEventType === 'pickup') {
    payload.pickup_at = dateInputValue(movedStart);
  } else {
    const duration = originalEnd
      ? originalEnd.getTime() - originalStart.getTime()
      : 0;
    const movedEnd = originalEnd
      ? new Date(movedStart.getTime() + duration)
      : null;
    payload.scheduled_at = dateInputValue(movedStart);
    payload.pickup_at = movedEnd ? dateInputValue(movedEnd) : null;
  }

  await ContractsAPI.update(event.contractData.id, payload);
  updateLocalContractCalendarEvent(event, payload);
};

const moveCalendarEventToDate = async (targetDate, dragEvent, hour = null) => {
  dragEvent.preventDefault();
  dragEvent.stopPropagation();

  const event = draggedCalendarEvent.value;
  clearCalendarDragState();
  if (!event || !isDraggableEvent(event) || !event.starts_at) return;

  const originalStart = new Date(event.starts_at);
  const originalEnd = event.ends_at ? new Date(event.ends_at) : null;
  const movedStart = new Date(targetDate);
  movedStart.setHours(
    hour === null ? originalStart.getHours() : hour,
    originalStart.getMinutes(),
    originalStart.getSeconds(),
    originalStart.getMilliseconds()
  );

  if (movedStart.getTime() === originalStart.getTime()) return;

  const duration = originalEnd
    ? originalEnd.getTime() - originalStart.getTime()
    : 0;
  const movedEnd = originalEnd
    ? new Date(movedStart.getTime() + duration)
    : null;

  try {
    if (event.source === 'contract') {
      await moveContractCalendarEvent(event, movedStart, originalStart);
    } else {
      const payload = {
        starts_at: dateInputValue(movedStart),
        ends_at: movedEnd ? dateInputValue(movedEnd) : '',
      };
      await TalkviaCalendarEventsAPI.update(event.id, payload);
      updateLocalCalendarEvent({
        ...event,
        starts_at: payload.starts_at,
        ends_at: payload.ends_at || null,
      });
    }
    useAlert(copy.messages.saved);
  } catch {
    useAlert(copy.messages.saveFailed);
  }
};
const eventSourceLabel = value => copy.sources[value] || value || '-';

const contractEventTypeLabel = value =>
  ({
    delivery: copy.contractModal.dataEntrega,
    pickup: copy.contractModal.dataRetirada,
  })[value] || copy.contractModal.label;

const buildContractCalendarEvents = contracts =>
  contracts.flatMap(contract => {
    const baseTitle = `${contract.recipient_name} · ${contract.template_name || 'Contrato'}`;

    if (contract.scheduled_at && contract.pickup_at) {
      return [
        {
          id: `contract-${contract.id}-period`,
          title: `Contrato: ${baseTitle}`,
          starts_at: contract.scheduled_at,
          ends_at: contract.pickup_at,
          source: 'contract',
          contact_id: contract.contact_id,
          contractData: contract,
          contractEventType: 'delivery',
        },
      ];
    }

    return [
      contract.scheduled_at && {
        id: `contract-${contract.id}-delivery`,
        title: `Entrega: ${baseTitle}`,
        starts_at: contract.scheduled_at,
        ends_at: contract.scheduled_at,
        source: 'contract',
        contact_id: contract.contact_id,
        contractData: contract,
        contractEventType: 'delivery',
      },
      contract.pickup_at && {
        id: `contract-${contract.id}-pickup`,
        title: `Retirada: ${baseTitle}`,
        starts_at: contract.pickup_at,
        ends_at: contract.pickup_at,
        source: 'contract',
        contact_id: contract.contact_id,
        contractData: contract,
        contractEventType: 'pickup',
      },
    ].filter(Boolean);
  });

const contractStatusLabel = status =>
  ({
    pending: 'Pendente',
    signed: 'Assinado',
    expired: 'Expirado',
    cancelled: 'Cancelado',
    awaiting_delivery: 'Aguardando entrega',
    completed: 'Concluído',
  })[status] || status;

const contractStatusColor = status =>
  ({
    pending: 'border-n-amber-5 bg-n-amber-3 text-n-amber-11',
    signed: 'border-n-teal-5 bg-n-teal-3 text-n-teal-11',
    expired: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    cancelled: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    awaiting_delivery: 'border-n-blue-5 bg-n-blue-3 text-n-blue-11',
    completed: 'border-n-green-5 bg-n-green-3 text-n-green-11',
    not_delivered: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
  })[status] || 'border-n-slate-5 bg-n-slate-3 text-n-slate-11';

const calendarStatusColor = status =>
  ({
    scheduled: 'bg-n-blue-3 text-n-blue-11',
    completed: 'bg-n-green-3 text-n-green-11',
    canceled: 'bg-n-ruby-3 text-n-ruby-11',
    no_show: 'bg-n-amber-3 text-n-amber-11',
  })[status] || 'bg-n-alpha-2 text-n-slate-12';

const contractMetadataAddress = contract => {
  const address = contract?.metadata?.recipient_address || {};
  const street = [address.address, address.number, address.complement]
    .filter(Boolean)
    .join(', ');
  const cityState = [address.city, address.state].filter(Boolean).join(' - ');

  return [street, address.neighborhood, cityState, address.zip_code]
    .filter(Boolean)
    .join(', ');
};

const selectedContactAddress = computed(() => {
  const contractAddress = contractMetadataAddress(selectedContract.value);
  if (contractAddress) return contractAddress;

  const attrs = selectedContactDetails.value?.additional_attributes;
  if (!attrs) return '';
  const {
    addressLine,
    addressNumber,
    addressComplement,
    neighborhood,
    city,
    state: uf,
    zipCode,
  } = attrs;
  const parts = [
    [addressLine, addressNumber, addressComplement].filter(Boolean).join(', '),
    neighborhood,
    [city, uf].filter(Boolean).join(' - '),
    zipCode,
  ].filter(Boolean);
  return parts.join(', ');
});

const mapUrl = provider => {
  if (!selectedContactAddress.value) return '';
  const query = encodeURIComponent(selectedContactAddress.value);
  if (provider === 'waze') return `https://waze.com/ul?q=${query}&navigate=yes`;
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

const selectMapContact = contact => {
  selectedMapContactId.value = contact.id;
  mapContactSearch.value =
    `${contact.name || 'Contato'} ${contact.phone_number || ''}`.trim();
  mapContactResults.value = [];
};

const searchMapContacts = async () => {
  if (mapContactSearch.value.trim().length < 2) {
    mapContactResults.value = [];
    return;
  }

  isSearchingMapContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(mapContactSearch.value, 1);
    mapContactResults.value = data.payload || [];
  } finally {
    isSearchingMapContacts.value = false;
  }
};

const handleMapContactSearch = () => {
  selectedMapContactId.value = '';
  searchMapContacts();
};

const sendMapToContact = async () => {
  if (!selectedContract.value?.id || !selectedContactAddress.value) return;

  try {
    await ContractsAPI.sendMapLink(selectedContract.value.id, {
      provider: mapProviderToSend.value,
      address: selectedContactAddress.value,
      contact_id: selectedMapContactId.value || null,
      inbox_id: selectedMapInboxId.value || null,
    });
    useAlert('Rota enviada na conversa.');
    showRouteSendModal.value = false;
  } catch {
    useAlert('Não foi possível enviar a rota para o contato.', {
      variant: 'error',
    });
  }
};

const mapProviderOptions = [
  { value: 'google', label: copy.contractModal.abrirGoogleMaps },
  { value: 'waze', label: copy.contractModal.abrirWaze },
];

const sendMapDisabled = computed(
  () =>
    !selectedContract.value?.id ||
    !selectedContactAddress.value ||
    !selectedMapContactId.value ||
    !selectedMapInboxId.value
);

const openRouteSendModal = () => {
  showRouteSendModal.value = true;
};

const closeRouteSendModal = () => {
  showRouteSendModal.value = false;
  mapContactResults.value = [];
};

const copyContractAddress = async () => {
  if (!selectedContactAddress.value) return;
  try {
    await navigator.clipboard.writeText(selectedContactAddress.value);
    useAlert('Endereço copiado.');
  } catch {
    useAlert('Não foi possível copiar o endereço.', { variant: 'error' });
  }
};

const openContractModal = async event => {
  selectedContract.value = event.contractData;
  selectedContractEventType.value = event.contractEventType || '';
  selectedContactDetails.value = null;
  pendingContractStatus.value = event.contractData?.status || '';
  selectedMapContactId.value = event.contractData?.contact_id || '';
  selectedMapInboxId.value = '';
  mapContactSearch.value =
    event.contractData?.contact_name ||
    event.contractData?.recipient_name ||
    '';
  mapContactResults.value = [];
  showContractModal.value = true;
  if (event.contractData?.contact_id) {
    try {
      const { data } = await ContactsAPI.show(event.contractData.contact_id);
      selectedContactDetails.value = data;
    } catch {
      // ignore
    }
  }
};

const goToContact = contactId => {
  showContractModal.value = false;
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

const goToContractEdit = () => {
  if (!selectedContract.value?.id) return;
  showContractModal.value = false;
  router.push({
    name: 'talkvia_contracts',
    query: { edit_contract_id: selectedContract.value.id },
  });
};

const eventColor = event => {
  if (event.source === 'contract') {
    return contractStatusColor(event.contractData?.status);
  }

  return calendarStatusColor(event.status);
};

const requestParams = () =>
  Object.fromEntries(
    Object.entries({
      ...filters,
      q: searchQuery.value.trim().length >= 2 ? searchQuery.value.trim() : '',
      start_at: requestRange.value.start.toISOString(),
      end_at: requestRange.value.end.toISOString(),
    }).filter(([, value]) => String(value || '').trim())
  );

const resetForm = (date = new Date()) => {
  editingEventId.value = null;
  contactSearch.value = '';
  contactResults.value = [];
  const startsAt = new Date(date);
  startsAt.setHours(9, 0, 0, 0);
  const endsAt = new Date(startsAt);
  endsAt.setMinutes(endsAt.getMinutes() + 30);
  Object.assign(eventForm, {
    title: '',
    description: '',
    location: '',
    starts_at: dateInputValue(startsAt),
    ends_at: dateInputValue(endsAt),
    all_day: false,
    status: 'scheduled',
    source: 'manual',
    contact_id: '',
    conversation_id: '',
    inbox_id: '',
    assignee_id: '',
    metadata: {
      inventory_product_id: '',
      inventory_quantity: '',
      create_google_meet: false,
      attendee_emails: '',
    },
  });
};

const openEventDialog = date => {
  resetForm(date);
  eventDialogRef.value?.open();
};

const openEventDialogAt = (date, hour = 9) => {
  const startsAt = new Date(date);
  startsAt.setHours(hour, 0, 0, 0);
  openEventDialog(startsAt);
};

const editEvent = event => {
  editingEventId.value = event.id;
  contactResults.value = event.contact ? [event.contact] : [];
  contactSearch.value = event.contact?.name || '';
  Object.assign(eventForm, {
    title: event.title,
    description: event.description || '',
    location: event.location || '',
    starts_at: event.starts_at ? dateInputValue(new Date(event.starts_at)) : '',
    ends_at: event.ends_at ? dateInputValue(new Date(event.ends_at)) : '',
    all_day: event.all_day,
    status: event.status,
    source: event.source,
    contact_id: event.contact_id || '',
    conversation_id: event.conversation_id || '',
    inbox_id: event.inbox_id || '',
    assignee_id: event.assignee_id || '',
    metadata: {
      ...event.metadata,
      inventory_product_id: event.metadata?.inventory_product_id || '',
      inventory_quantity: event.metadata?.inventory_quantity || '',
      create_google_meet: Boolean(
        event.metadata?.create_google_meet || event.metadata?.google_meet_link
      ),
      attendee_emails: Array.isArray(event.metadata?.attendee_emails)
        ? event.metadata.attendee_emails.join(', ')
        : event.metadata?.attendee_emails || '',
    },
  });
  eventDialogRef.value?.open();
};

const loadData = async ({ silent = false } = {}) => {
  if (isRefreshing.value) return;

  isRefreshing.value = true;
  if (!silent) {
    isLoading.value = true;
  }
  try {
    const [
      eventsResponse,
      integrationResponse,
      agentsResponse,
      productsResponse,
      contractsResponse,
    ] = await Promise.all([
      TalkviaCalendarEventsAPI.get(requestParams()),
      TalkviaCalendarIntegrationAPI.get().catch(() => {
        integrationLoadFailed.value = true;
        if (!silent) {
          useAlert(copy.messages.googleIntegrationLoadFailed, {
            variant: 'warning',
          });
        }
        return { data: null };
      }),
      AgentsAPI.get(),
      TalkviaInventoryProductsAPI.get({ active: true }),
      ContractsAPI.get({
        has_scheduled_at: true,
        start_at: requestRange.value.start.toISOString(),
        end_at: requestRange.value.end.toISOString(),
      }),
      store.dispatch('inboxes/get'),
    ]);
    events.value = eventsResponse.data || [];
    integration.value = integrationResponse.data || null;
    if (integrationResponse.data) integrationLoadFailed.value = false;
    agents.value = agentsResponse.data || [];
    inventoryProducts.value = productsResponse.data?.products || [];
    contractEvents.value = buildContractCalendarEvents(
      contractsResponse.data?.contracts || []
    );
  } catch {
    if (!silent) {
      useAlert(copy.messages.loadFailed);
    }
  } finally {
    isRefreshing.value = false;
    if (!silent) {
      isLoading.value = false;
    }
  }
};

const clearFilters = () => {
  const emptyFilters = {
    status: '',
    source: '',
    assignee_id: '',
    inbox_id: '',
  };
  Object.assign(filters, emptyFilters);
  Object.assign(filterDraft, emptyFilters);
  isFiltersOpen.value = false;
  loadData();
};

const applyFilters = () => {
  Object.assign(filters, filterDraft);
  isFiltersOpen.value = false;
  loadData();
};

const toggleFilters = () => {
  if (!isFiltersOpen.value) Object.assign(filterDraft, filters);
  isFiltersOpen.value = !isFiltersOpen.value;
};

const closeFilters = () => {
  isFiltersOpen.value = false;
};

const updateEventStart = value => {
  eventForm.starts_at = value ? dateInputValue(value) : '';

  if (!value) return;
  if (eventEndDate.value && eventEndDate.value > value) return;

  const suggestedEnd = new Date(value);
  suggestedEnd.setMinutes(suggestedEnd.getMinutes() + 30);
  eventForm.ends_at = dateInputValue(suggestedEnd);
};

const updateEventEnd = value => {
  eventForm.ends_at = value ? dateInputValue(value) : '';
};

const handleGoogleMeetToggle = () => {
  if (eventForm.metadata?.create_google_meet && !isGoogleCalendarActive.value) {
    useAlert(copy.messages.googleIntegrationRequired, { variant: 'warning' });
  }
};

const openGoogleCalendarSettings = () => {
  eventDialogRef.value?.close();
  router.push({ name: 'settings_integrations_google_calendar' });
};

const refreshCalendarSilently = () => {
  if (document.hidden) return;
  loadData({ silent: true });
};

const CONTRACT_STATUSES = [
  { value: 'pending', label: 'Pendente' },
  { value: 'signed', label: 'Assinado' },
  { value: 'awaiting_delivery', label: 'Aguardando entrega' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'expired', label: 'Expirado' },
];

const saveContractStatus = async () => {
  if (
    !selectedContract.value ||
    pendingContractStatus.value === selectedContract.value.status
  )
    return;
  contractStatusChanging.value = true;
  try {
    await ContractsAPI.updateStatus(
      selectedContract.value.id,
      pendingContractStatus.value
    );
    selectedContract.value = {
      ...selectedContract.value,
      status: pendingContractStatus.value,
    };
    useAlert('Status atualizado.');
    await loadData({ silent: true });
  } catch {
    useAlert('Não foi possível atualizar o status.', { variant: 'error' });
  } finally {
    contractStatusChanging.value = false;
  }
};

const refreshCalendar = () => {
  loadData();
};

const toggleSidePanel = () => {
  isSidePanelOpen.value = !isSidePanelOpen.value;
};

const openSearch = async () => {
  isSearchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
};

const closeSearch = () => {
  isSearchOpen.value = false;
  searchQuery.value = '';
  loadData({ silent: true });
};

const toggleSearch = () => {
  if (isSearchOpen.value) {
    closeSearch();
    return;
  }

  openSearch();
};

const closeViewMenu = () => {
  isViewMenuOpen.value = false;
};

const selectViewMode = mode => {
  viewMode.value = mode;
  closeViewMenu();
  loadData();
};

const selectDate = date => {
  currentDate.value = new Date(date);
  loadData();
};

const moveCalendar = step => {
  const date = new Date(currentDate.value);
  if (viewMode.value === 'day') {
    date.setDate(date.getDate() + step);
  } else if (viewMode.value === 'fourDays') {
    date.setDate(date.getDate() + step * 4);
  } else if (viewMode.value === 'month') {
    date.setMonth(date.getMonth() + step);
    date.setDate(1);
  } else if (viewMode.value === 'year') {
    date.setFullYear(date.getFullYear() + step);
  } else if (viewMode.value === 'schedule') {
    date.setMonth(date.getMonth() + step);
  } else {
    date.setDate(date.getDate() + step * 7);
  }
  currentDate.value = date;
  loadData();
};

const clearSearch = () => {
  searchQuery.value = '';
  searchInputRef.value?.focus();
  loadData({ silent: true });
};

watch(searchQuery, value => {
  if (searchTimer.value) window.clearTimeout(searchTimer.value);
  searchTimer.value = window.setTimeout(() => {
    if (!isSearchOpen.value) return;
    if (value.trim().length === 1) return;
    loadData({ silent: true });
  }, 350);
});

const startAutoRefresh = () => {
  if (refreshTimer.value) return;
  refreshTimer.value = window.setInterval(
    refreshCalendarSilently,
    AUTO_REFRESH_INTERVAL_MS
  );
};

const stopAutoRefresh = () => {
  if (!refreshTimer.value) return;
  window.clearInterval(refreshTimer.value);
  refreshTimer.value = null;
};

const handleVisibilityChange = () => {
  if (document.hidden) return;
  refreshCalendarSilently();
};

const saveEvent = async () => {
  if (
    !eventForm.ends_at ||
    new Date(eventForm.ends_at) <= new Date(eventForm.starts_at)
  ) {
    useAlert('Selecione um período válido, com o fim após o início.', {
      variant: 'warning',
    });
    return;
  }

  if (eventForm.metadata?.create_google_meet && !isGoogleCalendarActive.value) {
    useAlert(copy.messages.googleIntegrationRequired, { variant: 'warning' });
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      ...eventForm,
      contact_id: eventForm.contact_id || null,
      conversation_id: eventForm.conversation_id || null,
      inbox_id: eventForm.inbox_id || null,
      assignee_id: eventForm.assignee_id || null,
      metadata: {
        ...eventForm.metadata,
        inventory_product_id: eventForm.metadata?.inventory_product_id || '',
        inventory_quantity: eventForm.metadata?.inventory_quantity || '',
        create_google_meet: Boolean(eventForm.metadata?.create_google_meet),
        attendee_emails: eventForm.metadata?.attendee_emails || '',
        sync_google: Boolean(integration.value?.active),
      },
    };
    if (editingEventId.value) {
      await TalkviaCalendarEventsAPI.update(editingEventId.value, payload);
    } else {
      await TalkviaCalendarEventsAPI.create(payload);
    }
    useAlert(copy.messages.saved);
    eventDialogRef.value?.close();
    await loadData();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const searchContacts = async () => {
  if (!contactSearch.value || contactSearch.value.length < 2) {
    contactResults.value = [];
    return;
  }
  try {
    const { data } = await ContactsAPI.search(contactSearch.value, 1);
    contactResults.value = data.payload || [];
  } catch {
    contactResults.value = [];
  }
};

const selectContact = contact => {
  eventForm.contact_id = contact.id;
  contactSearch.value = contact.name || contact.phone_number || contact.email;
  contactResults.value = [contact];
};

const moveMonth = step => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + step,
    1
  );
  loadData();
};

const goToday = () => {
  currentDate.value = new Date(today);
  loadData();
};

onMounted(() => {
  loadData();
  startAutoRefresh();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  stopAutoRefresh();
  if (searchTimer.value) window.clearTimeout(searchTimer.value);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <section class="flex h-full w-full flex-col bg-n-solid-1 text-n-slate-12">
    <header
      class="relative z-50 flex min-h-[4.5rem] shrink-0 items-center gap-3 overflow-visible border-b border-n-weak bg-n-solid-1 px-4 py-3"
    >
      <button
        type="button"
        class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
        :class="isSidePanelOpen && 'bg-n-alpha-2 text-n-brand'"
        :aria-label="copy.actions.menu"
        @click="toggleSidePanel"
      >
        <span class="i-lucide-menu size-5" />
      </button>
      <span
        class="grid size-10 place-content-center rounded-xl bg-n-blue-9 text-lg font-bold text-white shadow-sm"
      >
        {{ today.getDate() }}
      </span>
      <h1 class="mb-0 mr-4 shrink-0 text-2xl font-normal text-n-slate-12">
        {{ copy.title }}
      </h1>

      <button
        type="button"
        class="h-10 rounded-full border border-n-strong px-5 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2"
        @click="goToday"
      >
        {{ copy.actions.today }}
      </button>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="grid size-9 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :title="copy.actions.previous"
          @click="moveCalendar(-1)"
        >
          <span class="i-lucide-chevron-left size-5" />
        </button>
        <button
          type="button"
          class="grid size-9 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :title="copy.actions.next"
          @click="moveCalendar(1)"
        >
          <span class="i-lucide-chevron-right size-5" />
        </button>
      </div>
      <h2
        class="mb-0 min-w-[12rem] flex-1 truncate text-2xl font-normal capitalize"
      >
        {{ rangeLabel }}
      </h2>
      <div class="ml-auto flex shrink-0 items-center gap-2">
        <div v-if="isSearchOpen" class="relative min-w-0">
          <div
            class="relative flex h-11 w-[min(32rem,42vw)] min-w-64 items-center gap-3 rounded-full border border-n-weak bg-n-solid-1 px-4 shadow-sm focus-within:border-n-brand/70"
          >
            <span
              class="pointer-events-none i-lucide-search size-4 text-n-slate-11"
            />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              class="block h-6 min-w-0 flex-1 appearance-none !rounded-none !border-0 !bg-transparent !p-0 text-sm leading-6 text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-9 focus:!border-0 focus:!shadow-none focus:!outline-none focus:!ring-0 focus-visible:!outline-none"
              :aria-label="copy.actions.search"
              :placeholder="copy.actions.search"
              @keydown.esc="closeSearch"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="copy.actions.clearSearch"
              @click="clearSearch"
            >
              <span class="i-lucide-x size-4" />
            </button>
            <button
              v-else
              type="button"
              class="grid size-6 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
              :aria-label="copy.actions.close"
              @click="closeSearch"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
          <div
            v-if="searchQuery.trim().length >= 2"
            class="absolute right-0 top-[3.25rem] z-[90] max-h-96 w-[26rem] overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-2 shadow-xl"
          >
            <button
              v-for="event in searchResults"
              :key="event.id"
              type="button"
              class="grid w-full gap-1 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="
                event.source === 'contract'
                  ? openContractModal(event)
                  : editEvent(event)
              "
            >
              <span class="truncate text-sm font-medium text-n-slate-12">
                {{ event.title }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatDateTime(event.starts_at) }}
              </span>
            </button>
            <p
              v-if="!searchResults.length && !isRefreshing"
              class="px-3 py-4 text-sm text-n-slate-10"
            >
              {{ copy.empty.search }}
            </p>
          </div>
        </div>
        <button
          v-if="!isSearchOpen"
          type="button"
          class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :aria-label="copy.actions.search"
          @click="toggleSearch"
        >
          <span class="i-lucide-search size-5" />
        </button>
        <button
          type="button"
          class="grid size-10 place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
          :class="isRefreshing && 'text-n-brand'"
          :title="copy.actions.sync"
          :disabled="isRefreshing"
          @click="refreshCalendar"
        >
          <span
            class="i-lucide-refresh-cw size-5"
            :class="isRefreshing && 'animate-spin'"
          />
        </button>
        <div v-if="isAdmin" v-on-clickaway="closeFilters" class="relative">
          <button
            type="button"
            class="relative inline-flex h-10 items-center gap-2 rounded-full border border-n-strong px-4 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2"
            :class="isFiltersOpen && 'border-n-brand bg-n-alpha-2 text-n-brand'"
            :aria-expanded="isFiltersOpen"
            @click="toggleFilters"
          >
            <span class="i-lucide-list-filter size-4" />
            {{ copy.actions.filters }}
            <span
              v-if="activeFilterCount"
              class="grid min-w-5 place-items-center rounded-full bg-n-brand px-1.5 py-0.5 text-[11px] leading-4 text-white"
            >
              {{ activeFilterCount }}
            </span>
          </button>

          <form
            v-if="isFiltersOpen"
            class="absolute right-0 top-12 z-[90] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl"
            @submit.prevent="applyFilters"
          >
            <div
              class="flex items-start justify-between gap-3 border-b border-n-weak px-4 py-3"
            >
              <div>
                <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ copy.sections.filters }}
                </h3>
                <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
                  <template v-if="activeFilterCount">
                    {{ activeFilterCount }} {{ copy.labels.activeFilters }}
                  </template>
                  <template v-else>{{ copy.labels.allEvents }}</template>
                </p>
              </div>
              <button
                v-if="activeFilterCount"
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                @click="clearFilters"
              >
                <span class="i-lucide-rotate-ccw size-3.5" />
                {{ copy.actions.clearFilters }}
              </button>
            </div>
            <div class="grid gap-3 p-4 sm:grid-cols-2">
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.status }}</span>
                <Select
                  v-model="filterDraft.status"
                  :options="filterStatusOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.source }}</span>
                <Select
                  v-model="filterDraft.source"
                  :options="filterSourceOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.assignee }}</span>
                <Select
                  v-model="filterDraft.assignee_id"
                  :options="filterAssigneeOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label class="grid min-w-0 gap-1.5">
                <span class="field-label">{{ copy.fields.inbox }}</span>
                <Select
                  v-model="filterDraft.inbox_id"
                  :options="filterInboxOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
            </div>
            <div
              class="flex justify-end border-t border-n-weak bg-n-alpha-1 px-4 py-3"
            >
              <button type="submit" class="btn btn-primary">
                {{ copy.actions.applyFilters }}
              </button>
            </div>
          </form>
        </div>
        <div class="relative">
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-full border border-n-strong px-4 text-sm font-semibold hover:bg-n-alpha-2"
            :class="isViewMenuOpen && 'bg-n-alpha-2 text-n-brand'"
            @click="isViewMenuOpen = !isViewMenuOpen"
          >
            {{ currentViewLabel }}
            <span class="i-lucide-chevron-down size-4" />
          </button>
          <div
            v-if="isViewMenuOpen"
            class="absolute right-0 top-12 z-[80] w-64 rounded-lg border border-n-weak bg-n-solid-1 p-2 shadow-xl"
          >
            <button
              v-for="option in viewOptions"
              :key="option.value"
              type="button"
              class="flex h-11 w-full items-center justify-between rounded-md px-3 text-left text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="selectViewMode(option.value)"
            >
              <span>{{ option.label }}</span>
              <span
                class="text-xs font-semibold uppercase text-n-slate-10"
                :class="option.value === viewMode && 'text-n-brand'"
              >
                <span
                  v-if="option.value === viewMode"
                  class="i-lucide-check size-4"
                />
                <span v-else>{{ option.shortcut }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <div
      class="grid min-h-0 flex-1 grid-cols-1 overflow-hidden"
      :class="
        isSidePanelOpen
          ? 'lg:grid-cols-[18rem_minmax(0,1fr)]'
          : 'lg:grid-cols-[minmax(0,1fr)]'
      "
    >
      <aside
        class="min-h-0 overflow-y-auto border-r border-n-weak bg-n-solid-1 px-4 py-4"
        :class="isSidePanelOpen ? 'block' : 'hidden'"
      >
        <button
          type="button"
          class="mb-6 inline-flex h-14 items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-5 text-sm font-semibold shadow-sm transition hover:shadow-md"
          @click="openEventDialog(new Date())"
        >
          <span class="i-lucide-plus size-6" />
          {{ copy.actions.create }}
        </button>

        <section class="mb-6">
          <div class="mb-3 flex items-center justify-between">
            <h3 class="mb-0 text-sm font-semibold capitalize">
              {{ monthLabel }}
            </h3>
            <div class="flex gap-1">
              <button
                type="button"
                class="grid size-7 place-content-center rounded-full hover:bg-n-alpha-2"
                @click="moveMonth(-1)"
              >
                <span class="i-lucide-chevron-left size-4" />
              </button>
              <button
                type="button"
                class="grid size-7 place-content-center rounded-full hover:bg-n-alpha-2"
                @click="moveMonth(1)"
              >
                <span class="i-lucide-chevron-right size-4" />
              </button>
            </div>
          </div>
          <div
            class="grid grid-cols-7 gap-y-1 text-center text-[11px] font-semibold text-n-slate-10"
          >
            <span v-for="weekday in copy.weekdays" :key="weekday">
              {{ weekday.charAt(0) }}
            </span>
            <button
              v-for="day in calendarDays"
              :key="day.key"
              type="button"
              class="mx-auto grid size-8 place-content-center rounded-full text-xs hover:bg-n-alpha-2"
              :class="[
                !day.currentMonth && 'text-n-slate-9',
                day.today && 'bg-n-brand text-white hover:bg-n-brand',
                day.date.toDateString() === currentDate.toDateString() &&
                  !day.today &&
                  'bg-n-alpha-2 text-n-brand',
              ]"
              @click="selectDate(day.date)"
            >
              {{ day.date.getDate() }}
            </button>
          </div>
        </section>

        <aside class="flex flex-col gap-4">
          <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <h3 class="mb-3 text-base font-semibold text-n-slate-12">
              {{ copy.sections.upcoming }}
            </h3>
            <div v-if="!upcomingEvents.length" class="text-sm text-n-slate-11">
              {{ copy.empty.events }}
            </div>
            <div v-else class="grid gap-2">
              <button
                v-for="event in upcomingEvents"
                :key="event.id"
                type="button"
                class="rounded-lg border bg-n-solid-1 p-3 text-left hover:bg-n-alpha-2"
                :class="
                  event.source === 'contract'
                    ? 'border-n-amber-5'
                    : 'border-n-weak'
                "
                @click="
                  event.source === 'contract'
                    ? openContractModal(event)
                    : editEvent(event)
                "
              >
                <p
                  class="mb-1 flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
                >
                  <span
                    v-if="event.source === 'contract'"
                    class="i-lucide-file-text size-3.5 shrink-0 text-n-amber-11"
                  />
                  {{ event.title }}
                </p>
                <p class="mb-1 text-xs text-n-slate-11">
                  {{ formatDateTime(event.starts_at) }}
                </p>
                <span
                  class="rounded-md px-2 py-1 text-xs"
                  :class="eventColor(event)"
                >
                  {{ eventSourceLabel(event.source) }}
                </span>
              </button>
            </div>
          </section>
        </aside>
      </aside>

      <main class="calendar-main min-w-0 w-full overflow-auto bg-n-solid-2">
        <div
          v-if="['day', 'week', 'fourDays'].includes(viewMode)"
          class="calendar-time-view w-full min-w-[720px]"
        >
          <div
            class="sticky top-0 z-20 grid border-b border-n-weak bg-n-solid-1/95 backdrop-blur"
            :style="{
              gridTemplateColumns: `5rem repeat(${visibleDays.length}, minmax(9rem, 1fr))`,
            }"
          >
            <div class="border-r border-n-weak" />
            <div
              v-for="day in visibleDays"
              :key="day.key"
              class="border-r border-n-weak px-3 py-2 text-center last:border-r-0"
            >
              <div
                class="mb-0.5 text-[11px] font-semibold uppercase tracking-wide"
                :class="day.today ? 'text-n-brand' : 'text-n-slate-10'"
              >
                {{ `${day.weekday}.` }}
              </div>
              <button
                type="button"
                class="mx-auto grid size-10 place-content-center rounded-full text-lg font-semibold"
                :class="
                  day.today
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="selectDate(day.date)"
              >
                {{ day.day }}
              </button>
            </div>
          </div>

          <div
            v-for="hour in timeSlots"
            :key="hour"
            class="grid min-h-[4rem] border-b border-n-weak"
            :style="{
              gridTemplateColumns: `5rem repeat(${visibleDays.length}, minmax(9rem, 1fr))`,
            }"
          >
            <div
              class="border-r border-n-weak pr-3 pt-1 text-right text-xs text-n-slate-10"
            >
              {{ formatHour(hour) }}
            </div>
            <button
              v-for="day in visibleDays"
              :key="`${day.key}-${hour}`"
              type="button"
              class="relative min-h-[4rem] border-r border-n-weak bg-transparent p-1 text-left last:border-r-0 hover:bg-n-alpha-1"
              :class="
                dragOverCalendarKey === calendarDropKey(day.date, hour) &&
                'bg-n-brand/10 ring-1 ring-inset ring-n-brand'
              "
              @click="openEventDialogAt(day.date, hour)"
              @dragover="handleCalendarEventDragOver(day.date, $event, hour)"
              @dragleave="handleCalendarEventDragLeave(day.date, hour)"
              @drop="moveCalendarEventToDate(day.date, $event, hour)"
            >
              <span
                v-for="event in eventsForHour(day.date, hour)"
                :key="event.id"
                class="calendar-event-chip mb-1 block w-full truncate rounded-md px-2 py-1 text-xs font-medium shadow-sm"
                :class="[
                  eventColor(event),
                  isDraggableEvent(event) &&
                    'cursor-grab active:cursor-grabbing',
                ]"
                :draggable="isDraggableEvent(event)"
                @dragstart.stop="startCalendarEventDrag(event, $event)"
                @dragend="clearCalendarDragState"
                @click.stop="
                  event.source === 'contract'
                    ? openContractModal(event)
                    : editEvent(event)
                "
              >
                <span class="font-semibold">
                  {{ formatTime(event.starts_at) }}
                </span>
                {{ event.title }}
              </span>
            </button>
          </div>
        </div>

        <div
          v-else-if="viewMode === 'month'"
          class="calendar-month-view w-full min-w-[760px]"
        >
          <div
            class="sticky top-0 z-30 grid grid-cols-7 border-b border-n-weak bg-n-solid-2"
          >
            <div
              v-for="weekday in copy.weekdays"
              :key="weekday"
              class="border-r border-n-weak px-3 py-2 text-center text-xs font-semibold uppercase text-n-slate-10 last:border-r-0"
            >
              {{ weekday }}
            </div>
          </div>
          <div
            v-for="(week, wi) in calendarWeeks"
            :key="wi"
            class="relative border-l border-n-weak"
          >
            <div class="grid grid-cols-7">
              <div
                v-for="day in week"
                :key="day.key"
                role="button"
                tabindex="0"
                class="min-h-40 border-b border-r border-n-weak bg-transparent px-2 py-2 text-left outline-none hover:bg-n-alpha-1 focus:bg-n-alpha-1"
                :class="[
                  !day.currentMonth && 'bg-n-alpha-1/40 text-n-slate-9',
                  dragOverCalendarKey === calendarDropKey(day.date) &&
                    'bg-n-brand/10 ring-1 ring-inset ring-n-brand',
                ]"
                @click="openEventDialogAt(day.date, 9)"
                @dragover="handleCalendarEventDragOver(day.date, $event)"
                @dragleave="handleCalendarEventDragLeave(day.date)"
                @drop="moveCalendarEventToDate(day.date, $event)"
                @keydown.enter="openEventDialogAt(day.date, 9)"
                @keydown.space.prevent="openEventDialogAt(day.date, 9)"
              >
                <time
                  class="grid size-7 place-content-center rounded-full text-sm font-medium"
                  :class="[
                    day.today ? 'bg-n-brand text-white' : 'text-n-slate-12',
                    !day.currentMonth && !day.today && 'text-n-slate-9',
                  ]"
                  :datetime="day.key"
                >
                  {{ day.date.getDate() }}
                </time>

                <div
                  class="mt-2"
                  :style="{
                    height: `${weekMultiDayRows(wi) * 28}px`,
                  }"
                />

                <div class="space-y-1">
                  <button
                    v-for="event in monthEventsForDate(day.date).slice(0, 4)"
                    :key="`${day.key}-${event.id}`"
                    type="button"
                    class="calendar-event-chip flex h-6 w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-md px-2 text-left text-xs font-medium"
                    :class="[
                      eventColor(event),
                      isDraggableEvent(event) &&
                        'cursor-grab active:cursor-grabbing',
                    ]"
                    :draggable="isDraggableEvent(event)"
                    :title="event.title"
                    @dragstart.stop="startCalendarEventDrag(event, $event)"
                    @dragend="clearCalendarDragState"
                    @click.stop="
                      event.source === 'contract'
                        ? openContractModal(event)
                        : editEvent(event)
                    "
                  >
                    <span class="shrink-0 font-semibold">
                      {{ formatTime(event.starts_at) }}
                    </span>
                    <span class="truncate">{{ event.title }}</span>
                  </button>

                  <span
                    v-if="hiddenMonthEventsCount(day.date)"
                    class="block px-2 pt-0.5 text-xs font-medium text-n-slate-10"
                  >
                    {{ `+${hiddenMonthEventsCount(day.date)} mais` }}
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="weekMultiDayRows(wi)"
              class="pointer-events-none absolute inset-x-0 top-[2.75rem]"
            >
              <button
                v-for="layout in weekLayouts[wi]"
                :key="layout.event.id"
                type="button"
                class="calendar-event-chip pointer-events-auto absolute flex h-6 items-center overflow-hidden px-2 text-left text-xs font-semibold shadow-sm"
                :class="[
                  eventColor(layout.event),
                  layout.startsThisWeek ? 'rounded-l-md' : '',
                  layout.endsThisWeek ? 'rounded-r-md' : '',
                  isDraggableEvent(layout.event) &&
                    'cursor-grab active:cursor-grabbing',
                ]"
                :title="`${layout.event.title} (${multiDayEventLabel(layout.event)})`"
                :draggable="isDraggableEvent(layout.event)"
                :style="{
                  top: `${layout.row * 28}px`,
                  left: `calc(${(layout.startCol / 7) * 100}% + 6px)`,
                  width: `calc(${(layout.colSpan / 7) * 100}% - 12px)`,
                }"
                @dragstart.stop="startCalendarEventDrag(layout.event, $event)"
                @dragend="clearCalendarDragState"
                @click="
                  layout.event.source === 'contract'
                    ? openContractModal(layout.event)
                    : editEvent(layout.event)
                "
              >
                <span
                  v-if="layout.startsThisWeek"
                  class="i-lucide-calendar-range mr-1.5 size-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span class="truncate">
                  {{
                    layout.startsThisWeek
                      ? `${layout.event.title} (${multiDayEventLabel(layout.event)})`
                      : ''
                  }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else-if="viewMode === 'year'"
          class="calendar-year-view grid gap-4 p-4 md:grid-cols-3 xl:grid-cols-4"
        >
          <section
            v-for="month in yearMonths"
            :key="month.key"
            class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
          >
            <button
              type="button"
              class="mb-3 text-left text-sm font-semibold capitalize text-n-slate-12 hover:text-n-brand"
              @click="
                currentDate = new Date(month.date);
                viewMode = 'month';
                loadData();
              "
            >
              {{ month.label }}
            </button>
            <div class="space-y-1">
              <button
                v-for="event in eventsForMonth(month.date).slice(0, 4)"
                :key="event.id"
                type="button"
                class="block w-full truncate rounded px-2 py-1 text-left text-xs"
                :class="eventColor(event)"
                @click="
                  event.source === 'contract'
                    ? openContractModal(event)
                    : editEvent(event)
                "
              >
                {{ `${new Date(event.starts_at).getDate()} · ${event.title}` }}
              </button>
              <p
                v-if="!eventsForMonth(month.date).length"
                class="text-xs text-n-slate-10"
              >
                {{ copy.empty.events }}
              </p>
            </div>
          </section>
        </div>

        <div
          v-else
          class="calendar-schedule-view mx-auto grid max-w-4xl gap-2 p-4"
        >
          <button
            v-for="event in scheduleEvents"
            :key="event.id"
            type="button"
            class="flex items-center gap-4 border-b border-n-weak bg-transparent px-2 py-3 text-left hover:bg-n-alpha-1"
            @click="
              event.source === 'contract'
                ? openContractModal(event)
                : editEvent(event)
            "
          >
            <span class="w-36 shrink-0 text-sm text-n-slate-10">
              {{ formatDateTime(event.starts_at) }}
            </span>
            <span class="min-w-0 flex-1 truncate text-sm font-medium">
              {{ event.title }}
            </span>
            <span class="rounded px-2 py-1 text-xs" :class="eventColor(event)">
              {{ eventSourceLabel(event.source) }}
            </span>
          </button>
          <p v-if="!scheduleEvents.length" class="p-6 text-sm text-n-slate-11">
            {{ copy.empty.events }}
          </p>
        </div>
      </main>
    </div>

    <Dialog
      ref="eventDialogRef"
      width="4xl"
      position="center"
      overflow-y-auto
      :title="editingEventId ? copy.actions.edit : copy.actions.newEvent"
      :description="
        editingEventId
          ? 'Atualize os dados e vínculos deste compromisso.'
          : 'Crie um compromisso e conecte-o ao atendimento do cliente.'
      "
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="grid min-w-0 gap-5">
        <section class="event-form-section">
          <div class="event-form-section__header">
            <span class="event-form-section__icon i-lucide-calendar-clock" />
            <div>
              <h4 class="event-form-section__title">
                {{ copy.sections.eventDetails }}
              </h4>
              <p class="event-form-section__description">
                {{ copy.sections.eventDetailsDescription }}
              </p>
            </div>
          </div>
          <div class="grid min-w-0 gap-4 p-4 md:grid-cols-2">
            <Input
              v-model="eventForm.title"
              required
              :label="copy.fields.title"
              :placeholder="copy.placeholders.title"
              class="md:col-span-2"
            />
            <div class="grid gap-3 md:col-span-2">
              <span class="field-label">{{ copy.fields.period }}</span>
              <div class="grid min-w-0 gap-4 md:grid-cols-2">
                <div class="grid min-w-0 gap-1.5">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ copy.fields.start }}
                  </span>
                  <DateTimePicker
                    :value="eventStartDate"
                    :confirm-text="copy.actions.apply"
                    :placeholder="copy.fields.start"
                    :disable-past="false"
                    @change="updateEventStart"
                  />
                </div>
                <div class="grid min-w-0 gap-1.5">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ copy.fields.end }}
                  </span>
                  <DateTimePicker
                    :value="eventEndDate"
                    :confirm-text="copy.actions.apply"
                    :placeholder="copy.fields.end"
                    :disable-past="false"
                    @change="updateEventEnd"
                  />
                </div>
              </div>
              <span class="text-xs text-n-slate-10">
                {{ copy.placeholders.periodHelp }}
              </span>
            </div>
            <label
              class="inline-flex w-fit items-center gap-2 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm font-medium text-n-slate-11 md:col-span-2"
            >
              <input
                v-model="eventForm.all_day"
                type="checkbox"
                class="size-4 accent-[rgb(var(--color-n-brand))]"
              />
              <span class="i-lucide-sun size-4 text-n-amber-10" />
              {{ copy.fields.allDay }}
            </label>
          </div>
        </section>

        <section class="event-form-section">
          <div class="event-form-section__header">
            <span class="event-form-section__icon i-lucide-users-round" />
            <div>
              <h4 class="event-form-section__title">
                {{ copy.sections.organization }}
              </h4>
              <p class="event-form-section__description">
                {{ copy.sections.organizationDescription }}
              </p>
            </div>
          </div>
          <div class="grid min-w-0 gap-4 p-4 md:grid-cols-2">
            <div class="grid min-w-0 gap-1.5">
              <span class="field-label">{{ copy.fields.status }}</span>
              <Select
                v-model="eventForm.status"
                :options="statusOptions"
                class="w-full [&>select]:h-10 [&>select]:w-full"
              />
            </div>
            <div class="grid min-w-0 gap-1.5">
              <span class="field-label">{{ copy.fields.source }}</span>
              <Select
                v-model="eventForm.source"
                :options="sourceOptions"
                class="w-full [&>select]:h-10 [&>select]:w-full"
              />
            </div>
            <div class="grid min-w-0 gap-1.5">
              <span class="field-label">{{ copy.fields.assignee }}</span>
              <Select
                v-model="eventForm.assignee_id"
                :options="assigneeOptions"
                class="w-full [&>select]:h-10 [&>select]:w-full"
              />
            </div>
            <div class="grid min-w-0 gap-1.5">
              <span class="field-label">{{ copy.fields.inbox }}</span>
              <Select
                v-model="eventForm.inbox_id"
                :options="eventInboxOptions"
                class="w-full [&>select]:h-10 [&>select]:w-full"
              />
            </div>
            <div class="relative grid gap-1.5">
              <Input
                v-model="contactSearch"
                :label="copy.fields.contactSearch"
                :placeholder="copy.placeholders.contactSearch"
                @input="searchContacts"
              />
              <div
                v-if="contactResults.length"
                class="absolute left-0 right-0 top-full z-20 mt-1 max-h-44 overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-xl"
              >
                <button
                  v-for="contact in contactResults"
                  :key="contact.id"
                  type="button"
                  class="flex w-full flex-col rounded-lg px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                  @click="selectContact(contact)"
                >
                  <span class="font-medium text-n-slate-12">
                    {{ contact.name || contact.phone_number || contact.email }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{
                      contact.phone_number ||
                      contact.email ||
                      contact.identifier
                    }}
                  </span>
                </button>
              </div>
            </div>
            <Input
              v-model="eventForm.location"
              :label="copy.fields.location"
              :placeholder="copy.placeholders.location"
            />
          </div>
        </section>

        <section class="event-form-section">
          <div class="event-form-section__header">
            <span class="event-form-section__icon i-lucide-package-plus" />
            <div>
              <h4 class="event-form-section__title">
                {{ copy.sections.service }}
              </h4>
              <p class="event-form-section__description">
                {{ copy.sections.serviceDescription }}
              </p>
            </div>
          </div>
          <div class="grid min-w-0 gap-4 p-4 md:grid-cols-2">
            <div class="grid min-w-0 gap-1.5">
              <span class="field-label">{{
                copy.fields.inventoryProduct
              }}</span>
              <Select
                v-model="eventForm.metadata.inventory_product_id"
                :options="productOptions"
                class="w-full [&>select]:h-10 [&>select]:w-full"
              />
            </div>
            <Input
              v-model="eventForm.metadata.inventory_quantity"
              type="number"
              min="1"
              :label="copy.fields.inventoryQuantity"
              :placeholder="copy.placeholders.inventoryQuantity"
            />
          </div>
        </section>

        <section class="event-form-section">
          <div class="event-form-section__header">
            <span class="event-form-section__icon i-lucide-video" />
            <div>
              <h4 class="event-form-section__title">
                {{ copy.sections.meeting }}
              </h4>
              <p class="event-form-section__description">
                {{ copy.sections.meetingDescription }}
              </p>
            </div>
          </div>
          <div class="grid min-w-0 gap-4 p-4">
            <div
              class="grid gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <label
                  class="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-n-slate-12"
                >
                  <input
                    v-model="eventForm.metadata.create_google_meet"
                    type="checkbox"
                    class="size-4 accent-n-brand"
                    @change="handleGoogleMeetToggle"
                  />
                  <span
                    class="grid size-8 place-items-center rounded-lg bg-n-blue-3 text-n-blue-10"
                  >
                    <span class="i-lucide-video size-4" />
                  </span>
                  {{ copy.fields.createGoogleMeet }}
                </label>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                  :class="
                    isGoogleCalendarActive
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  <span
                    class="size-1.5 rounded-full"
                    :class="
                      isGoogleCalendarActive ? 'bg-n-teal-9' : 'bg-n-amber-9'
                    "
                  />
                  {{
                    isGoogleCalendarActive
                      ? copy.labels.connected
                      : copy.labels.googleUnavailable
                  }}
                </span>
              </div>

              <div
                v-if="
                  integrationLoadFailed ||
                  (eventForm.metadata.create_google_meet &&
                    !isGoogleCalendarActive)
                "
                class="flex flex-col gap-3 rounded-xl border border-n-amber-5 bg-n-amber-2 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="flex min-w-0 items-start gap-2.5">
                  <span
                    class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0 text-n-amber-10"
                  />
                  <div>
                    <p class="mb-0 text-sm font-medium text-n-amber-12">
                      {{ copy.sections.googleIntegration }}
                    </p>
                    <p class="mb-0 mt-0.5 text-xs text-n-amber-11">
                      {{
                        integrationLoadFailed
                          ? copy.messages.googleIntegrationLoadFailed
                          : copy.messages.googleIntegrationRequired
                      }}
                    </p>
                  </div>
                </div>
                <button
                  v-if="isAdmin"
                  type="button"
                  class="btn btn-ghost shrink-0"
                  @click="openGoogleCalendarSettings"
                >
                  {{ copy.actions.configureGoogle }}
                </button>
              </div>

              <Input
                v-model="eventForm.metadata.attendee_emails"
                :label="copy.fields.attendeeEmails"
                :placeholder="copy.placeholders.attendeeEmails"
                :disabled="!eventForm.metadata.create_google_meet"
              />
              <a
                v-if="googleMeetLink"
                :href="googleMeetLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex w-fit items-center gap-2 text-sm font-medium text-n-blue-10 hover:text-n-blue-11"
              >
                <span class="i-lucide-external-link size-4" />
                {{ copy.fields.googleMeetLink }}
              </a>
            </div>
          </div>
        </section>

        <section class="event-form-section p-4">
          <label class="grid gap-1.5">
            <span class="field-label">{{ copy.sections.notes }}</span>
            <textarea
              v-model="eventForm.description"
              rows="3"
              class="form-input min-h-24 resize-y"
              :placeholder="copy.placeholders.notes"
            />
          </label>
        </section>

        <div
          class="sticky -bottom-6 z-30 -mx-6 -mb-6 flex w-auto justify-end gap-3 border-t border-n-weak bg-n-solid-1/95 px-6 py-4 backdrop-blur"
        >
          <button
            type="button"
            class="btn btn-ghost"
            @click="eventDialogRef?.close()"
          >
            {{ copy.actions.cancel }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isSaving || !eventForm.title || !eventForm.starts_at"
            @click="saveEvent"
          >
            {{ copy.actions.saveEvent }}
          </button>
        </div>
      </div>
    </Dialog>

    <Teleport to="body">
      <div
        v-if="showContractModal && selectedContract"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-md"
        @click.self="showContractModal = false"
      >
        <section
          class="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div class="min-w-0">
              <p
                class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-amber-11"
              >
                {{ copy.contractModal.label }}
              </p>
              <h2 class="mb-1 truncate text-lg font-semibold text-n-slate-12">
                {{ selectedContract.recipient_name }}
              </h2>
              <span
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                :class="contractStatusColor(selectedContract.status)"
              >
                {{ contractStatusLabel(selectedContract.status) }}
              </span>
              <span
                v-if="selectedContractEventType"
                class="ml-2 inline-flex items-center rounded-full border border-n-amber-5 bg-n-amber-3 px-2.5 py-0.5 text-xs font-medium text-n-amber-11"
              >
                {{ contractEventTypeLabel(selectedContractEventType) }}
              </span>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="showContractModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div class="grid gap-4 px-6 py-5">
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.modelo
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ selectedContract.template_name || '-' }}
                </span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.contato
                }}</span>
                <div class="flex items-center gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      selectedContract.contact_name ||
                      selectedContract.recipient_name
                    }}
                  </span>
                  <button
                    v-if="selectedContract.contact_id"
                    v-tooltip.top="copy.contractModal.verContato"
                    type="button"
                    class="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-n-brand hover:bg-n-alpha-2"
                    :title="copy.contractModal.verContato"
                    @click="goToContact(selectedContract.contact_id)"
                  >
                    <span class="i-lucide-user-round-search size-4" />
                    <span class="sr-only">{{
                      copy.contractModal.verContato
                    }}</span>
                  </button>
                </div>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.dataEntrega
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    selectedContract.scheduled_at
                      ? new Date(selectedContract.scheduled_at).toLocaleString(
                          'pt-BR',
                          {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          }
                        )
                      : copy.labels.emptyValue
                  }}
                </span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.dataRetirada
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    selectedContract.pickup_at
                      ? new Date(selectedContract.pickup_at).toLocaleString(
                          'pt-BR',
                          {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          }
                        )
                      : copy.labels.emptyValue
                  }}
                </span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.formaPagamento
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    selectedContract.payment_method || copy.labels.emptyValue
                  }}
                </span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.valorTotal
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    selectedContract.metadata?.invoice_total
                      ? Number(
                          selectedContract.metadata.invoice_total
                        ).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })
                      : copy.labels.emptyValue
                  }}
                </span>
              </div>
              <div
                v-if="selectedContract.recipient_phone"
                class="flex flex-col gap-0.5"
              >
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.telefone
                }}</span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ selectedContract.recipient_phone }}
                </span>
              </div>
              <div
                v-if="selectedContactAddress"
                class="col-span-2 flex flex-col gap-0.5"
              >
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.endereco
                }}</span>
                <div class="flex items-start gap-2">
                  <a
                    :href="mapUrl('google')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex min-w-0 items-start gap-1 text-sm font-medium text-n-brand hover:underline"
                    @click.stop
                  >
                    <span class="i-lucide-map-pin mt-0.5 size-3.5 shrink-0" />
                    <span class="min-w-0 break-words">
                      {{ selectedContactAddress }}
                    </span>
                  </a>
                  <button
                    v-tooltip.top="copy.contractModal.copiarEndereco"
                    type="button"
                    class="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-brand"
                    :title="copy.contractModal.copiarEndereco"
                    @click="copyContractAddress"
                  >
                    <span class="i-lucide-copy size-4" />
                    <span class="sr-only">
                      {{ copy.contractModal.copiarEndereco }}
                    </span>
                  </button>
                </div>
              </div>
              <div
                v-if="selectedContactAddress"
                class="col-span-2 flex flex-col gap-2"
              >
                <span class="text-xs text-n-slate-10">{{
                  copy.contractModal.mapas
                }}</span>
                <div class="grid gap-2 sm:grid-cols-3">
                  <a
                    :href="mapUrl('google')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <span class="i-lucide-map-pinned size-4" />
                    {{ copy.contractModal.abrirGoogleMaps }}
                  </a>
                  <a
                    :href="mapUrl('waze')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <span class="i-lucide-navigation size-4" />
                    {{ copy.contractModal.abrirWaze }}
                  </a>
                  <button
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                    @click="openRouteSendModal"
                  >
                    <span class="i-lucide-send size-4" />
                    {{ copy.contractModal.enviarRota }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="selectedContract.metadata?.invoice_items?.length"
              class="flex flex-col gap-2"
            >
              <span class="text-xs text-n-slate-10">{{
                copy.contractModal.produtos
              }}</span>
              <div class="overflow-hidden rounded-lg border border-n-weak">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-n-weak bg-n-alpha-1">
                      <th
                        class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                      >
                        {{ copy.contractModal.produto }}
                      </th>
                      <th
                        class="px-3 py-2 text-center text-xs font-medium text-n-slate-10"
                      >
                        {{ copy.contractModal.qtd }}
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                      >
                        {{ copy.contractModal.valorUnit }}
                      </th>
                      <th
                        class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                      >
                        {{ copy.contractModal.subtotal }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in selectedContract.metadata
                        .invoice_items"
                      :key="idx"
                      class="border-b border-n-weak last:border-0"
                    >
                      <td class="px-3 py-2 font-medium text-n-slate-12">
                        {{ item.product_name || '-' }}
                      </td>
                      <td class="px-3 py-2 text-center text-n-slate-11">
                        {{ item.quantity }}
                      </td>
                      <td class="px-3 py-2 text-right text-n-slate-11">
                        {{
                          Number(item.unit_price || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        }}
                      </td>
                      <td
                        class="px-3 py-2 text-right font-medium text-n-slate-12"
                      >
                        {{
                          Number(
                            (item.quantity || 0) * (item.unit_price || 0)
                          ).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="border-t border-n-weak px-6 py-4">
            <p class="mb-2 text-xs font-semibold text-n-slate-10">
              {{ copy.contractModal.alterarStatus }}
            </p>
            <div
              class="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-stretch"
            >
              <div class="relative min-w-0">
                <select
                  v-model="pendingContractStatus"
                  class="calendar-select h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                >
                  <option
                    v-for="opt in CONTRACT_STATUSES"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <button
                type="button"
                class="inline-flex h-10 w-full items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95 disabled:opacity-60 sm:h-full sm:w-auto"
                :disabled="
                  contractStatusChanging ||
                  pendingContractStatus === selectedContract.status
                "
                @click="saveContractStatus"
              >
                <span
                  v-if="contractStatusChanging"
                  class="i-lucide-loader-circle size-4 animate-spin"
                />
                <span v-else>{{ copy.contractModal.confirmar }}</span>
              </button>
            </div>
          </div>
          <footer
            class="flex items-center justify-end gap-3 border-t border-n-weak bg-n-solid-2 px-6 py-4"
          >
            <button
              type="button"
              class="inline-flex h-10 w-full items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 sm:w-auto"
              @click="showContractModal = false"
            >
              {{ copy.contractModal.fechar }}
            </button>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="goToContractEdit"
            >
              <span class="i-lucide-pencil-line size-4" />
              {{ copy.contractModal.editarContrato }}
            </button>
            <a
              v-if="selectedContract.public_url"
              :href="selectedContract.public_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95"
            >
              <span class="i-lucide-external-link size-4" />
              {{ copy.contractModal.verContrato }}
            </a>
          </footer>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showRouteSendModal && selectedContract"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-md"
        @click.self="closeRouteSendModal"
      >
        <section
          class="flex w-full max-w-md flex-col overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
          >
            <div class="min-w-0">
              <p
                class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-amber-11"
              >
                {{ copy.contractModal.mapas }}
              </p>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ copy.contractModal.enviarRota }}
              </h2>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="closeRouteSendModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div class="grid gap-4 px-6 py-5">
            <div class="relative">
              <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                {{ copy.contractModal.enviarPara }}
                <input
                  v-model="mapContactSearch"
                  class="h-10 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                  :placeholder="copy.contractModal.buscarContato"
                  @input="handleMapContactSearch"
                />
              </label>
              <div
                v-if="mapContactResults.length"
                class="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
              >
                <button
                  v-for="contact in mapContactResults"
                  :key="contact.id"
                  type="button"
                  class="grid w-full gap-0.5 border-b border-n-weak px-3 py-2 text-left text-sm last:border-b-0 hover:bg-n-alpha-2"
                  @click="selectMapContact(contact)"
                >
                  <span class="font-medium text-n-slate-12">
                    {{ contact.name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ contact.phone_number || '-' }}
                  </span>
                </button>
              </div>
              <p
                v-if="isSearchingMapContacts"
                class="mb-0 mt-1 text-xs text-n-slate-10"
              >
                {{ copy.contractModal.buscandoContatos }}
              </p>
            </div>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ copy.contractModal.caixaEntrada }}
              <select
                v-model="selectedMapInboxId"
                class="calendar-select h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option value="">
                  {{ copy.contractModal.selecionarCaixaEntrada }}
                </option>
                <option
                  v-for="inbox in inboxOptions"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ copy.contractModal.mapas }}
              <select
                v-model="mapProviderToSend"
                class="calendar-select h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option
                  v-for="option in mapProviderOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>

          <footer
            class="flex items-center justify-end gap-3 border-t border-n-weak px-6 py-4"
          >
            <button
              type="button"
              class="inline-flex h-10 w-full items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 sm:w-auto"
              @click="closeRouteSendModal"
            >
              {{ copy.contractModal.fechar }}
            </button>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95 disabled:opacity-60"
              :disabled="sendMapDisabled"
              @click="sendMapToContact"
            >
              <span class="i-lucide-send size-4" />
              {{ copy.contractModal.enviarRota }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped>
.event-form-section {
  @apply overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2;
}

.event-form-section__header {
  @apply flex items-start gap-3 border-b border-n-weak bg-n-alpha-1 px-4 py-3;
}

.event-form-section__icon {
  @apply mt-0.5 size-4 shrink-0 text-n-brand;
}

.event-form-section__title {
  @apply mb-0 text-sm font-semibold text-n-slate-12;
}

.event-form-section__description {
  @apply mb-0 mt-0.5 text-xs leading-5 text-n-slate-10;
}

.field-label {
  @apply mb-0.5 text-sm font-medium text-n-slate-12;
}
</style>
